"use client";

import React, { useEffect, useState, useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";

import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import { Navigation } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";

import { parseManagedFeaturesList } from "../../lib/function";
import { Button } from "react-bootstrap";
import { FaArrowRight, FaPlus, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { FaTimes } from "react-icons/fa";

/* -------------------- COUNTRY LIST -------------------- */
const allCountries = [
  { code: "us", name: "USA", slug: "usa", fullName: "New York, USA" },
  { code: "us", name: "San Fr", slug: "san", fullName: "San Francisco, USA" },
  { code: "nl", name: "NL", slug: "netherlands", fullName: "Netherlands" },
  { code: "sg", name: "SG", slug: "singapore", fullName: "Singapore" },
  { code: "de", name: "DE", slug: "germany", fullName: "Germany" },
  { code: "gb", name: "UK", slug: "unitedkingdom", fullName: "United Kingdom" },
  { code: "ca", name: "CA", slug: "canada", fullName: "Canada" },
  { code: "in", name: "IN", slug: "india", fullName: "India" },
  { code: "my", name: "MY", slug: "malaysia", fullName: "Malaysia" },
  { code: "it", name: "IT", slug: "italy", fullName: "Italy" },
  { code: "ae", name: "AE", slug: "uae", fullName: "UAE" },
  { code: "sa", name: "SA", slug: "saudiarabia", fullName: "Saudi Arabia" },
  { code: "id", name: "ID", slug: "indonesia", fullName: "Indonesia" },
];

const ServerPackagesLvps = () => {
  const [packagesData, setPackagesData] = useState<Record<string, any[]>>({});
  const [isMobile, setIsMobile] = useState(false);

  const pathname = usePathname();
  const rawSlug = pathname?.split("/").pop() || "";
  const slug = pathname?.split("-").pop();

  const swiperRef = useRef<SwiperType | null>(null);

  const selectedCountry =
    allCountries.find((c) => c.slug === slug) || allCountries[0];

  /* Detect mobile */
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  /* Fetch Packages */
  useEffect(() => {
    fetch("/api/plan", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ rawSlug }),
    })
      .then((r) => r.json())
      .then((response) => {
        const map: Record<string, any[]> = {};

        allCountries.forEach((country) => {
          map[country.code] = response.data.slice(0, 20).map((item: any) => ({
            id: item.id.toString(),
            name: item.name,
            price: `$${item.duration.monthly.price}`,
            oldPrice: `$${item.duration.monthly.actuall_price.toFixed(2)}`,
            discount:
              item.duration.monthly.discount > 0
                ? `Save ${item.duration.monthly.discount}%`
                : "0",
            term: `Price per ${item.duration.monthly.desc}`,
            features: parseManagedFeaturesList(item.description, slug),
            configureUrl: `${process.env.NEXT_PUBLIC_API_BASE_URL}/cart.php?a=add&pid=${item.id}&billingcycle=${item.duration.monthly.type}`,
            country: country.code,
          }));
        });

        setPackagesData(map);
      });
  }, [rawSlug, slug]);

  /* Fix modal cleanup */
  const closeModalBeforeNavigate = () => {
    document.body.classList.remove("modal-open");
    document.body.style.overflow = "";
    document.body.style.paddingRight = "";
    const backs = document.querySelectorAll(".modal-backdrop");
    backs.forEach((b) => b.remove());
  };

  /* Mouse wheel scroll behavior for flags */
  const setupWheelScroll = (swiper: SwiperType) => {
    swiperRef.current = swiper;

    const wrapper = swiper.wrapperEl;

    wrapper.addEventListener(
      "wheel",
      (e: WheelEvent) => {
        e.preventDefault(); // ❗ Stop page scroll
        if (e.deltaY > 0) swiper.slideNext();
        else swiper.slidePrev();
      },
      { passive: false }
    );
  };

  /* -------------------- RETURN JSX -------------------- */
  return (
    <div className="custMargin">
      <div className="container mt-4">

        {/* PAGE TITLE */}
        <div className="text-center my-4">
          <h1 className="display-5 fw-bold">
            Linux VPS / Cloud Servers in{" "}
            <span className="text-capitalize">{selectedCountry.fullName}</span>
          </h1>
          <p className="fs-5 fw-normal">Backed by 24/7 On-Premise Support</p>
        </div>

        {/* -------------------- DESKTOP SWIPER -------------------- */}
        {!isMobile && (
          <>
            <h6
              className="fw-bold fs-5 mb-3 text-center d-flex justify-content-center align-items-center gap-2"
              style={{ cursor: "pointer" }}
              data-bs-toggle="modal"
              data-bs-target="#lvpsCountryModal"
            >
              All Locations <FaPlus className="fs-6" />
            </h6>

            <div className="w-75 mx-auto mb-5">
              <div className="position-relative d-flex align-items-center">

                {/* LEFT ARROW */}
                <button
                  className="lvps-arrow-left"
                  style={{
                    position: "absolute",
                    left: "-15px",
                    zIndex: 20,
                    width: "36px",
                    height: "36px",
                    borderRadius: "50%",
                    background: "#fff",
                    border: "1px solid #ddd",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                  }}
                >
                  <FaChevronLeft />
                </button>

                {/* RIGHT ARROW */}
                <button
                  className="lvps-arrow-right"
                  style={{
                    position: "absolute",
                    right: "-15px",
                    zIndex: 20,
                    width: "36px",
                    height: "36px",
                    borderRadius: "50%",
                    background: "#fff",
                    border: "1px solid #ddd",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                  }}
                >
                  <FaChevronRight />
                </button>

                {/* FLAG SWIPER (Wheel-controlled) */}
                <div style={{ width: "100%", padding: "0 40px" }}>
                  <Swiper
                    slidesPerView="auto"
                    spaceBetween={20}
                    modules={[Navigation]}
                    navigation={{
                      nextEl: ".lvps-arrow-right",
                      prevEl: ".lvps-arrow-left",
                    }}
                    loop={true}
                    simulateTouch={false}
                    allowTouchMove={false}
                    onSwiper={setupWheelScroll} // attach wheel scroll
                  >
                    {allCountries.map((c) => {
                      const active = c.slug === slug;
                      return (
                        <SwiperSlide key={c.slug} style={{ width: "70px" }}>
                          <Link
                            href={`/linux-virtual-private-server-vps-${c.slug}`}
                            className={`text-decoration-none d-block text-center countryItem py-2 textHad ${
                              active ? "activeCountry" : ""
                            }`}
                          >
                            <Image
                              src={`https://flagcdn.com/w40/${c.code}.png`}
                              width={32}
                              height={22}
                              alt={c.fullName}
                            />
                            <div className="fw-bold mt-1">{c.name}</div>
                          </Link>
                        </SwiperSlide>
                      );
                    })}
                  </Swiper>
                </div>
              </div>
            </div>
          </>
        )}

        {/* -------------------- MOBILE DROPDOWN -------------------- */}
        {isMobile && (
          <div className="dropdown w-100 mb-4">
            <h6
              className="fw-bold fs-5 mb-3 text-center d-flex justify-content-center gap-2"
              data-bs-toggle="modal"
              data-bs-target="#lvpsCountryModal"
              style={{ cursor: "pointer" }}
            >
              All Locations <FaPlus />
            </h6>

            <button className="form-select p-3 fw-semibold LocCont">
              <span className="d-flex align-items-center gap-2">
                <Image
                  src={`https://flagcdn.com/w40/${selectedCountry.code}.png`}
                  width={25}
                  height={18}
                  alt={selectedCountry.fullName}
                />
                {selectedCountry.fullName}
              </span>
            </button>
          </div>
        )}

        {/* -------------------- COUNTRY MODAL -------------------- */}
        <div className="modal fade" id="lvpsCountryModal" tabIndex={-1}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content p-4">
              <div className="modal-header border-0  d-flex justify-content-between">
                <h4 className="modal-title fw-bold">Choose Location</h4>
                <Button
  type="button"
  className="border-0 bg-transparent small float-end me-0"
  data-bs-dismiss="modal"
  aria-label="Close"
>
  <FaTimes className="textHad"/> 
</Button>
              </div>

              <div className="modal-body">
                <div className="row g-3">
                  {allCountries.map((c) => (
                    <div
                      key={c.slug}
                      className="col-6 col-md-4 d-flex align-items-center gap-2"
                    >
                      <Image
                        src={`https://flagcdn.com/w40/${c.code}.png`}
                        width={28}
                        height={20}
                        alt={c.fullName}
                      />
                      <Link
                        href={`/linux-virtual-private-server-vps-${c.slug}`}
                        className="fw-semibold text-dark text-decoration-none"
                        onClick={closeModalBeforeNavigate}
                      >
                        {c.fullName}
                      </Link>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* -------------------- PACKAGES -------------------- */}
        <div className="row justify-content-center">
          {(packagesData[selectedCountry.code] || []).map((pkg) => (
            <div key={pkg.id} className="col-lg-3 col-md-6 col-12 mb-4">
              <div className="card-body h-100">

                <h3 className="card-title fw-bold fs-5 textOrange">{pkg.name}</h3>

                <p className="text-muted">{pkg.term}</p>

                <div className="d-flex align-items-end mb-2">
                  <span className="display-6 fw-bold">{pkg.price}</span>
                  <span className="ms-1 text-muted">/mo</span>
                </div>

                {pkg.discount !== "0" && (
                  <h4 className="mt-2 fw-normal text-muted">
                    <del className="fw-bold">{pkg.oldPrice}</del>
                    <span className="alert alert-warning py-1 px-2 ms-2 fw-semibold">
                      {pkg.discount}
                    </span>
                  </h4>
                )}

                <div className="text-start mb-2 mt-3">
                  <Image
                    src={`https://flagcdn.com/w40/${pkg.country}.png`}
                    width={30}
                    height={22}
                    alt={pkg.country}
                  />
                  <span className="ms-2 fw-bold">{selectedCountry.fullName}</span>
                </div>

                <Link href={pkg.configureUrl}>
                  <Button size="lg" className="orangeBtn fw-semibold w-100">
                    ORDER <FaArrowRight />
                  </Button>
                </Link>

                <h4 className="fs-5 fw-bold mt-3">Features</h4>
                <ul className="list-unstyled custList">
                  {pkg.features.map((f: string, idx: number) => (
                    <li key={idx}>
                      <span className="text-success">✔</span>{" "}
                      <span className="fw-semibold textHad">{f}</span>
                    </li>
                  ))}
                </ul>

              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ServerPackagesLvps;
